<template>
  <div class="bg p-3 pb-24 mb-auto h-full overflow-auto scrollbar-custom" id="style-1">
    <div class="max-w-full mx-auto">
      <!-- Header -->
      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center gap-3">
          <i class="fas fa-history text-indigo-300 text-xl"></i>
          <div>
            <h1 class="text-2xl font-bold text-indigo-300">ประวัติตลาด</h1>
          </div>
        </div>
      </div>
      <!-- Messages -->
      <div v-if="error" class="rounded-md border border-red-700 bg-red-900 bg-opacity-10 text-red-200 p-3 mb-3 text-sm">
        <i class="fas fa-times-circle mr-1"></i> {{ error }}
      </div>
      <!-- Loading skeleton -->
      <div v-if="isFetching" class="grid grid-cols-1 gap-3">
        <div v-for="n in 10" :key="'s'+n" class="h-20 rounded-md border border-gray-700 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 bg-opacity-30 animate-pulse"></div>
      </div>
      <!-- Logs List -->
      <div v-else>
        <div v-if="logs.length" class="space-y-3">
          <div
            v-for="log in logs"
            :key="log.id"
            class="border border-gray-700 rounded-md bg-gray-900 bg-opacity-80 p-3"
          >
            <div class="flex items-center justify-between mb-2">
              <div class="text-sm font-semibold text-indigo-200">
                <i class="fas fa-clock mr-1"></i> {{ formatTimestamp(log.timestamp) }}
              </div>
              <span class="chip" :class="getActionClass(log.action)">
                {{ log.action }}
              </span>
            </div>
            <div class="text-sm text-gray-300 mb-2">
              <i class="fas fa-user mr-1"></i> ผู้ใช้: {{ log.uid }}
            </div>
            <div class="text-sm text-gray-200 mb-2">
              <i class="fas fa-info-circle mr-1"></i> ข้อความ: {{ log.displayMessage }}
            </div>
            <div class="text-xs text-gray-400">
              <i class="fas fa-file-alt mr-1"></i> รายละเอียด:
              <pre class="bg-gray-800 p-2 rounded-md mt-1 overflow-auto">{{ formatDetails(log.details) }}</pre>
            </div>
            <div class="text-xs mt-2" :class="log.success ? 'text-green-300' : 'text-red-300'">
              <i class="fas" :class="log.success ? 'fa-check' : 'fa-times'"></i> สถานะ: {{ log.success ? 'สำเร็จ' : 'ล้มเหลว' }}
              <span v-if="!log.success && log.error"> (ข้อผิดพลาด: {{ log.error }})</span>
            </div>
          </div>
        </div>
        <div v-else class="border border-dashed border-gray-700 rounded-md p-6 text-center bg-gray-800 bg-opacity-60">
          <i class="fas fa-history text-3xl text-indigo-300 mb-2"></i>
          <h2 class="text-lg font-semibold text-indigo-200">ไม่มีประวัติการทำธุรกรรม</h2>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';

export default {
  name: 'MarketHistory',
  setup() {
    const { user } = getUser();
    const isFetching = ref(true);
    const error = ref(null);
    const logs = ref([]);
    let unsubscribe = null;

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return 'ไม่ระบุ';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    };

    const formatDetails = (details) => {
      return JSON.stringify(details, null, 2);
    };

    const getActionClass = (action) => {
      switch (action) {
        case 'setMarketplace':
          return 'chip-green';
        case 'buyItem':
          return 'chip-amber';
        case 'itemSold':
          return 'chip-blue';
        case 'closeShop':
          return 'chip-muted';
        default:
          return 'chip';
      }
    };

    const attachRealtime = () => {
      if (unsubscribe) { unsubscribe(); unsubscribe = null; }
      // Assuming only admin can access this page; add auth check if needed
      if (!user.value) {
        error.value = 'กรุณาล็อกอินเพื่อดูประวัติ';
        isFetching.value = false;
        return;
      }
      // Query logs for marketplace actions, ordered by timestamp desc
      unsubscribe = projectFirestore
        .collection('logs')
        .where('action', 'in', ['setMarketplace', 'buyItem', 'itemSold', 'closeShop','updateShop'])
        .orderBy('timestamp', 'desc')
        .limit(100) // Limit to recent 100 logs; can add pagination if needed
        .onSnapshot((snap) => {
          logs.value = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          isFetching.value = false;
        }, (e) => {
          error.value = e.message || 'ไม่สามารถโหลดประวัติได้';
          isFetching.value = false;
        });
    };

    onMounted(() => {
      attachRealtime();
    });

    onBeforeUnmount(() => {
      if (unsubscribe) unsubscribe();
    });

    return {
      isFetching,
      error,
      logs,
      formatTimestamp,
      formatDetails,
      getActionClass,
    };
  },
};
</script>

<style scoped>
/* Background */
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}

/* Scrollbar */
.scrollbar-custom::-webkit-scrollbar { width: 6px; }
.scrollbar-custom::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
.scrollbar-custom::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
.scrollbar-custom::-webkit-scrollbar-thumb:hover { background: #6b7280; }

/* Chips */
.chip {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  line-height: 1rem;
  border: 1px solid #374151;
  background: #111827;
  color: #c7caff;
}
.chip-green {
  background: rgba(6, 78, 59, 0.3);
  color: #bbf7d0;
  border-color: #065f46;
}
.chip-amber {
  background: rgba(120, 53, 15, 0.2);
  color: #fde68a;
  border-color: #92400e;
}
.chip-blue {
  background: rgba(30, 58, 138, 0.3);
  color: #bfdbfe;
  border-color: #1e40af;
}
.chip-muted {
  background: #111827;
  color: #9ca3af;
  border-color: #374151;
}

/* text-2xs */
.text-2xs { font-size: 0.7rem; line-height: 0.95rem; }

/* Pre for details */
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>